import { useEffect, useState } from "react";
import { Target, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function GoalDialog({ open, onOpenChange, promptId, promptTitle, onSaved }) {
    const [goal, setGoal] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !promptId) return;
        api.get(`/goals/${promptId}`)
            .then(({ data }) => setGoal(data))
            .catch(() => toast.error("Couldn't load goals"));
    }, [open, promptId]);

    if (!open) return null;

    const update = (patch) => setGoal((g) => ({ ...g, ...patch }));

    const save = async () => {
        if (!goal) return;
        if (Number(goal.target_wpm_min) > Number(goal.target_wpm_max)) {
            toast.error("Min WPM can't exceed max WPM");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.put(`/goals/${promptId}`, {
                target_wpm_min: Number(goal.target_wpm_min),
                target_wpm_max: Number(goal.target_wpm_max),
                max_fillers_per_min: Number(goal.max_fillers_per_min),
                target_duration_sec: Number(goal.target_duration_sec),
            });
            toast.success("Goals saved");
            onSaved?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save goals");
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        setSaving(true);
        try {
            const { data } = await api.delete(`/goals/${promptId}`);
            setGoal(data);
            onSaved?.(data);
            toast.success("Reset to defaults");
        } catch {
            toast.error("Couldn't reset");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-3xl max-w-md" data-testid="goal-dialog">
                <DialogHeader>
                    <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-terracotta">
                        <Target size={12} strokeWidth={1.5} /> Practice goals
                    </div>
                    <DialogTitle className="font-serif text-2xl font-light leading-tight tracking-tight">
                        {promptTitle}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-stone-500">
                        We'll grade your next runs against these. Recommended pace is 120–160 WPM.
                    </DialogDescription>
                </DialogHeader>

                {!goal ? (
                    <div className="text-stone-500 italic text-sm py-6 text-center">Loading…</div>
                ) : (
                    <div className="space-y-5">
                        <div>
                            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">Words per minute</Label>
                            <div className="mt-2 flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="60"
                                    max="260"
                                    value={goal.target_wpm_min}
                                    onChange={(e) => update({ target_wpm_min: e.target.value })}
                                    className="rounded-2xl"
                                    data-testid="goal-wpm-min"
                                />
                                <span className="text-xs text-stone-400">to</span>
                                <Input
                                    type="number"
                                    min="60"
                                    max="260"
                                    value={goal.target_wpm_max}
                                    onChange={(e) => update({ target_wpm_max: e.target.value })}
                                    className="rounded-2xl"
                                    data-testid="goal-wpm-max"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">Max fillers per minute</Label>
                            <Input
                                type="number"
                                min="0"
                                max="30"
                                step="0.5"
                                value={goal.max_fillers_per_min}
                                onChange={(e) => update({ max_fillers_per_min: e.target.value })}
                                className="mt-2 rounded-2xl"
                                data-testid="goal-fillers"
                            />
                            <div className="text-xs text-stone-400 mt-1">e.g. 2 means roughly two "ums" per minute is your ceiling.</div>
                        </div>

                        <div>
                            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">Target duration (seconds)</Label>
                            <Input
                                type="number"
                                min="10"
                                max="600"
                                value={goal.target_duration_sec}
                                onChange={(e) => update({ target_duration_sec: e.target.value })}
                                className="mt-2 rounded-2xl"
                                data-testid="goal-duration"
                            />
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 sm:gap-2 mt-2 sm:justify-between">
                    <Button
                        variant="ghost"
                        onClick={reset}
                        disabled={saving || !goal}
                        className="rounded-full text-stone-500"
                        data-testid="goal-reset"
                    >
                        <RotateCcw size={14} className="mr-1.5" /> Reset
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving || !goal}
                        className="rounded-full bg-terracotta hover:bg-[#D05A40] text-white"
                        data-testid="goal-save"
                    >
                        {saving ? "Saving…" : "Save goals"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Helper: classify how a metric performed against the goal
// Returns one of: "good" | "near" | "off"
export function compareGoal(metric, goal, kind) {
    if (!goal) return "good";
    if (kind === "wpm") {
        const v = Number(metric);
        const lo = Number(goal.target_wpm_min);
        const hi = Number(goal.target_wpm_max);
        if (v >= lo && v <= hi) return "good";
        if (v >= lo - 15 && v <= hi + 15) return "near";
        return "off";
    }
    if (kind === "fillers_per_min") {
        const v = Number(metric);
        const cap = Number(goal.max_fillers_per_min);
        if (v <= cap) return "good";
        if (v <= cap + 1.5) return "near";
        return "off";
    }
    if (kind === "duration") {
        const v = Number(metric);
        const target = Number(goal.target_duration_sec);
        const window = Math.max(15, target * 0.25);
        const diff = Math.abs(v - target);
        if (diff <= window) return "good";
        if (diff <= window * 2) return "near";
        return "off";
    }
    return "good";
}
